import { createContext, ReactNode, useContext, useState } from 'react';
import {
  HandlersCreateProgramDeliverableTypeRequest,
  HandlersProgramDeliverableTypeResponse,
} from '../../../../../app/athenaApiGen';
import { EMPTY_PAYLOAD } from './consts';

// Internal context shape — kept as { id, body } to avoid touching all the
// AddTypeModal / UpdateTypeModal / ProgramTypesTable references to `payload.body`.
// The translation to the new POST request shape (handlersProgramDeliverableTypesRequest.deliverableTypes)
// happens at the mutation call site. Was previously typed as
// PutApiProgramsByIdDeliverableTypesApiArg, which the codegen removed in the
// Pattern B migration (PUT-whole-set → DELETE-per-row + POST-per-row).
type ProgramTypesPayload = {
  id: string;
  body: HandlersCreateProgramDeliverableTypeRequest[];
};

type TProgramTypesContext = {
  addTypeModal: boolean;
  setAddTypeModal: React.Dispatch<React.SetStateAction<boolean>>;
  changeNumberModal: boolean;
  setChangeNumberModal: React.Dispatch<React.SetStateAction<boolean>>;
  updateTypeModal: boolean;
  setUpdateTypeModal: React.Dispatch<React.SetStateAction<boolean>>;
  payload: ProgramTypesPayload;
  setPayload: React.Dispatch<
    React.SetStateAction<ProgramTypesPayload>
  >;
  programDelType: (
    | HandlersCreateProgramDeliverableTypeRequest
    | HandlersProgramDeliverableTypeResponse
  )[];
  setProgramDelType: React.Dispatch<
    React.SetStateAction<
      (
        | HandlersCreateProgramDeliverableTypeRequest
        | HandlersProgramDeliverableTypeResponse
      )[]
    >
  >;
};

const ProgramTypesContext = createContext<TProgramTypesContext | undefined>(
  undefined,
);

const ProgramTypesProviders: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [addTypeModal, setAddTypeModal] = useState<boolean>(false);
  const [changeNumberModal, setChangeNumberModal] = useState<boolean>(false);
  const [updateTypeModal, setUpdateTypeModal] = useState<boolean>(false);
  const [payload, setPayload] =
    useState<ProgramTypesPayload>(EMPTY_PAYLOAD);
  const [programDelType, setProgramDelType] = useState<
    (
      | HandlersCreateProgramDeliverableTypeRequest
      | HandlersProgramDeliverableTypeResponse
    )[]
  >([]);

  const contextValue = {
    addTypeModal,
    setAddTypeModal,
    payload,
    setPayload,
    programDelType,
    setProgramDelType,
    changeNumberModal,
    setChangeNumberModal,
    updateTypeModal,
    setUpdateTypeModal,
  };

  return (
    <ProgramTypesContext.Provider value={contextValue}>
      {children}
    </ProgramTypesContext.Provider>
  );
};

const useProgramTypeContext = () => {
  const context = useContext(ProgramTypesContext);
  if (context === undefined) {
    throw new Error(
      'useProgramTypeContext must be used within a ProgramTypesProvider',
    );
  }
  return context;
};

export { ProgramTypesProviders, useProgramTypeContext };
